import posthog from "posthog-js";
import { shouldSuppressKnownBrowserError } from "./analytics/error-noise";
import { createLogger } from "./logger";

const logger = createLogger("error-tracking");

interface ErrorContext {
  [key: string]: any;
}

const isPlainDomEvent = (input: unknown): input is Event => {
  return (
    typeof Event !== "undefined" &&
    input instanceof Event &&
    !(typeof ErrorEvent !== "undefined" && input instanceof ErrorEvent)
  );
};

declare global {
  var __GLOBAL_ERROR_HANDLERS_INSTALLED__: boolean | undefined;
}

/**
 * Enhanced error tracking with context
 */
/**
 * Normalize any input into a proper Error object
 */
export const normalizeError = (input: any): Error => {
  if (input instanceof Error) {
    return input;
  }

  // Handle ErrorEvent (common in window.onerror)
  if (
    typeof ErrorEvent !== "undefined" &&
    input instanceof ErrorEvent &&
    input.error instanceof Error
  ) {
    return input.error;
  }

  // Handle ErrorEvent without inner error
  if (typeof ErrorEvent !== "undefined" && input instanceof ErrorEvent) {
    return new Error(input.message || "Unknown ErrorEvent");
  }

  // Handle DOM Events (e.g. resource loading errors)
  if (typeof Event !== "undefined" && input instanceof Event) {
    const target = input.target as HTMLElement;
    const tagName = target?.tagName || "UNKNOWN_TARGET";
    const type = input.type || "unknown";

    // Resource loading errors often strictly have type "error" and a src/href
    if (
      type === "error" &&
      (target instanceof HTMLImageElement || target instanceof HTMLScriptElement)
    ) {
      const src = (target as any).src || (target as any).href || "";
      return new Error(`ResourceLoadError: Failed to load ${tagName} from ${src}`);
    }

    return new Error(`DOMError: Captured event mismatch (${type}) on ${tagName}`);
  }

  // Handle string messages
  if (typeof input === "string") {
    return new Error(input);
  }

  // Handle plain objects with message property
  if (typeof input === "object" && input !== null && "message" in input) {
    return new Error(String(input.message));
  }

  // Fallback
  return new Error(String(input));
};

/**
 * Extract useful context from DOM Events that aren't errors
 */
export const extractEventContext = (input: any): Record<string, any> => {
  if (!input || typeof input !== "object") return {};

  const context: Record<string, any> = {};

  // Extract properties from Event/ErrorEvent
  if (typeof Event !== "undefined" && input instanceof Event) {
    context.domEvent = {
      type: input.type,
      isTrusted: input.isTrusted,
      timeStamp: input.timeStamp,
    };

    const target = input.target as HTMLElement;
    if (target) {
      context.domTarget = {
        tagName: target.tagName,
        id: target.id,
        className: target.className,
        name: (target as any).name,
        src: (target as any).src,
        href: (target as any).href,
      };
    }
  }

  // Extract specific ErrorEvent properties
  if (typeof ErrorEvent !== "undefined" && input instanceof ErrorEvent) {
    Object.assign(context, {
      filename: input.filename,
      lineno: input.lineno,
      colno: input.colno,
    });
  }

  return context;
};

/**
 * Enhanced error tracking with context
 */
export const trackError = (input: Error | any, context: ErrorContext = {}) => {
  if (isPlainDomEvent(input)) {
    logger.warn("Ignoring DOM Event passed to exception tracking", {
      type: input.type,
      isTrusted: input.isTrusted,
    });
    return;
  }

  const error = normalizeError(input);
  if (shouldSuppressKnownBrowserError(error)) {
    return;
  }

  const eventContext = extractEventContext(input);

  // Get app-specific context
  const appContext = {
    userLanguage: typeof window !== "undefined" ? localStorage.getItem("language") : null,
    pathname: typeof window !== "undefined" ? window.location.pathname : null,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    timestamp: new Date().toISOString(),
    ...eventContext,
    ...context,
  };

  const exceptionProperties = {
    $exception_personalized: true,
    ...appContext,
  };

  // Send to PostHog using the SDK's exception API when available.
  if (typeof (posthog as any).captureException === "function") {
    (posthog as any).captureException(error, exceptionProperties);
  } else {
    posthog.capture("$exception", {
      $exception_message: error.message,
      $exception_type: error.name,
      $exception_stack_trace_raw: error.stack,
      ...exceptionProperties,
    });
  }

  // Also log to console in development
  if (process.env.NODE_ENV === "development") {
    logger.error("Error tracked", { error: error.message, ...appContext });
  }
};

/**
 * Track WebRTC specific errors with connection state
 */
export const trackWebRTCError = (
  error: Error,
  connection: RTCPeerConnection | null,
  additionalContext: ErrorContext = {}
) => {
  const webrtcContext = {
    connectionState: connection?.connectionState,
    signalingState: connection?.signalingState,
    iceConnectionState: connection?.iceConnectionState,
    iceGatheringState: connection?.iceGatheringState,
    errorType: "webrtc",
    ...additionalContext,
  };

  trackError(error, webrtcContext);
};

/**
 * Track DOM manipulation errors
 */
export const trackDOMError = (error: Error, element: HTMLElement | null, operation: string) => {
  trackError(error, {
    errorType: "dom",
    operation,
    elementTag: element?.tagName,
    elementId: element?.id,
    elementClass: element?.className,
    hasParent: !!element?.parentNode,
  });
};

/**
 * Track chunk loading errors
 */
export const trackChunkLoadError = (error: Error, chunkId?: string) => {
  trackError(error, {
    errorType: "chunk_load",
    chunkId,
    url: (error as any)?.request || "unknown",
  });
};

/**
 * Setup global error handlers
 */
/**
 * Setup global error handlers
 */
export const setupGlobalErrorHandlers = () => {
  if (typeof window === "undefined") return;
  if (globalThis.__GLOBAL_ERROR_HANDLERS_INSTALLED__) return;
  globalThis.__GLOBAL_ERROR_HANDLERS_INSTALLED__ = true;

  // Handle uncaught errors
  window.addEventListener("error", (event) => {
    // Ignore chunk errors here as they are handled by the capturing listener below
    // or by the specific handling logic to ensure we don't double track.
    if (
      event.message &&
      (event.message.includes("Loading chunk") || event.message.includes("ChunkLoadError"))
    ) {
      return;
    }

    trackError(event.error || new Error(event.message), {
      source: "window.onerror",
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    if (isPlainDomEvent(event.reason)) {
      event.preventDefault();
      trackError(event.reason, {
        source: "unhandledrejection",
        promise: true,
      });
      return;
    }

    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));

    if (shouldSuppressKnownBrowserError(error)) {
      event.preventDefault();
      return;
    }

    trackError(event.reason instanceof Error ? event.reason : error, {
      source: "unhandledrejection",
      promise: true,
    });
  });

  // Handle chunk loading errors specifically (capturing phase for resource errors)
  window.addEventListener(
    "error",
    (event) => {
      const message = event.message || "";
      if (message.includes("Loading chunk") || message.includes("ChunkLoadError")) {
        const chunkMatch = message.match(/chunk (\d+)/);
        trackChunkLoadError(new Error(message), chunkMatch ? chunkMatch[1] : undefined);

        // Note: UI logic for reloading should be handled by a separate subscriber
        // to keep this utility pure.
      }
    },
    true
  );
};
